/**
 * Per-employee effective-dated compensation (salary history) -- self-fetching
 * hook mirroring `use_employee_deductions.ts`: real bearer client
 * (`getTrpcClient()`), `useOAuthEntityContext()`, `isWriteCapableGrant` gate,
 * never-throw mutation results. Three procedures (all OAuth-allowlisted,
 * `payroll:employer:read`/`write`):
 * - `payroll.listEmployeeCompensation` -- the append-only history + today's pay
 * - `payroll.scheduleCompensationChange` -- record a (possibly backdated or
 *   future-dated) salary / rate / basis / currency change
 * - `payroll.cancelCompensationChange` -- cancel a not-yet-consumed change
 *
 * Money is integer minor units end to end (docs/money-rules.md); this module
 * never converts -- `MoneyInput` owns the conversion at the form boundary.
 * No mock fallback: failures surface as explicit states, never fabricated data.
 *
 * Mutation failures are classified into five buckets, not two, because the
 * backend distinguishes them and each needs different copy:
 * - `conflict` -- a DRAFT pay run overlaps the change, an entry already exists
 *   on that date, or a currency change would start inside a pay period. Carries
 *   the backend's own caller-safe message (see `isConflictTrpcError`).
 * - `consumed` -- an approved/paid pay run already used the entry, so it can no
 *   longer be cancelled (record a correcting entry instead).
 * - `validation` -- any other BAD_REQUEST (before the hire date, after
 *   termination, ...), backend message shown verbatim.
 * - `entitlement_required` / `error` -- as everywhere else in this app.
 */
import * as React from 'react';
import {
  extractLedgerCode,
  getTrpcClient,
  isBadRequestTrpcError,
  isConflictTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

export type CompensationPayBasis = 'salaried' | 'hourly';
export type CompensationReason =
  | 'hire'
  | 'promotion'
  | 'annual_review'
  | 'correction'
  | 'other';
export type CompensationRowStatus = 'active' | 'cancelled' | 'superseded';

/** The `ledgerCode`s `apps/api/src/lib/error_codes.ts` attaches to compensation rejections. */
export const COMPENSATION_LEDGER_CODES = {
  draft_overlap: 'LEDGER_PAYROLL_DRAFT_PAY_RUN_OVERLAPS_COMPENSATION',
  same_date: 'LEDGER_PAYROLL_COMPENSATION_SAME_DATE',
  consumed: 'LEDGER_PAYROLL_COMPENSATION_CONSUMED',
  currency_mid_period: 'LEDGER_PAYROLL_COMPENSATION_CURRENCY_MID_PERIOD',
} as const;

/** One `employee_compensation` row as returned by `payroll.listEmployeeCompensation`. */
export interface EmployeeCompensationRecord {
  id: string;
  employee_id: string;
  /** ISO `YYYY-MM-DD`, inclusive first day the row applies. */
  effective_from: string;
  pay_basis: CompensationPayBasis;
  /** Minor units; `null` for an hourly row. */
  monthly_salary_cents: number | null;
  /** Minor units per hour; `0` on a salaried row. */
  hourly_rate_cents: number;
  /** ISO 4217, or `null` = the organisation base currency. */
  pay_currency_code: string | null;
  reason: CompensationReason;
  note: string | null;
  status: CompensationRowStatus;
  /** `manual` | `legacy_update` | `baseline` | `reconstructed`. */
  source: string;
  created_at: string;
}

/** What the employee is paid today (`effective_today` in the list response). */
export interface EffectiveCompensation {
  pay_basis: CompensationPayBasis;
  monthly_salary_cents: number | null;
  hourly_rate_cents: number;
  pay_currency_code: string | null;
  /** The `employee_compensation.id` it came from; `null` = the employee record itself. */
  compensation_id: string | null;
}

export interface EmployeeCompensationFetchInput {
  firm_id: string;
  entity_id: string;
  employee_id: string;
  include_inactive?: boolean;
}

export interface EmployeeCompensationListResult {
  rows: EmployeeCompensationRecord[];
  has_history: boolean;
  effective_today: EffectiveCompensation;
}

export type CompensationLoadFailure = 'entitlement_required' | 'error';

export type CompensationState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | {
      status: 'ready';
      rows: EmployeeCompensationRecord[];
      has_history: boolean;
      effective_today: EffectiveCompensation;
    };

export interface EmployeeCompensationFetchDeps {
  listEmployeeCompensation: (
    input: EmployeeCompensationFetchInput,
  ) => Promise<EmployeeCompensationListResult>;
}

function defaultFetchDeps(): EmployeeCompensationFetchDeps {
  const client = getTrpcClient();
  return {
    listEmployeeCompensation: (input) =>
      client.payroll.listEmployeeCompensation.query(input),
  };
}

export type FetchCompensationResult =
  | { outcome: 'success'; data: EmployeeCompensationListResult }
  | { outcome: 'failure'; failure: CompensationLoadFailure };

function classifyLoadFailure(err: unknown): CompensationLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

/** Never throws -- every failure resolves a classified {@link FetchCompensationResult}. */
export async function fetchEmployeeCompensation(
  input: EmployeeCompensationFetchInput,
  deps?: EmployeeCompensationFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<FetchCompensationResult> {
  try {
    const resolvedDeps = deps ?? defaultFetchDeps();
    const data = await resolvedDeps.listEmployeeCompensation(input);
    return { outcome: 'success', data };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load employee compensation:', err);
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export function resolveCompensationState(input: {
  entityContext: OAuthEntityContext;
  data: EmployeeCompensationListResult | undefined;
  failure: CompensationLoadFailure | undefined;
}): CompensationState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.data !== undefined) {
    return {
      status: 'ready',
      rows: input.data.rows,
      has_history: input.data.has_history,
      effective_today: input.data.effective_today,
    };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path ──────────────────────────────────────────────────────────────

/** Input fields for `payroll.scheduleCompensationChange` (minus the entity/employee ids). */
export interface ScheduleCompensationInput {
  pay_basis: CompensationPayBasis;
  /** Minor units. Required for a salaried change. */
  monthly_salary_cents?: number | null;
  /** Minor units per hour. Required (positive) for an hourly change. */
  hourly_rate_cents?: number;
  /** `undefined` keeps the pay currency in effect; `null` resets to the organisation base. */
  pay_currency_code?: string | null;
  effective_from: string;
  reason: CompensationReason;
  note?: string;
  /** Supersede an existing entry dated the same day instead of failing with `same_date`. */
  replace_same_date?: boolean;
}

/** A finalised pay run a backdated change reaches into (settled later as back pay). */
export interface AffectedPayRun {
  pay_run_id: string;
  period_start: string;
  period_end: string;
  status: string;
}

export interface ScheduleCompensationResponse {
  compensation_id: string;
  applied_to_cache: boolean;
  superseded_id: string | null;
  back_pay_preview: { finalised_pay_runs: AffectedPayRun[] } | null;
}

export type CompensationConflictReason =
  | 'draft_overlap'
  | 'same_date'
  | 'currency_mid_period'
  | 'other';

export type CompensationMutationFailure =
  | {
      kind: 'conflict';
      reason: CompensationConflictReason;
      message: string;
    }
  | { kind: 'consumed'; message: string }
  | { kind: 'validation'; message: string }
  | { kind: 'entitlement_required' }
  | { kind: 'error' };

export type ScheduleCompensationResult =
  | {
      outcome: 'success';
      /** `true` when the change took effect immediately (dated today or earlier). */
      applied_now: boolean;
      /** Finalised pay runs the change reaches back into; empty when none. */
      back_pay_pay_runs: AffectedPayRun[];
    }
  | { outcome: 'failure'; failure: CompensationMutationFailure };

export type CancelCompensationResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: CompensationMutationFailure };

function conflictReason(code: string | null): CompensationConflictReason {
  switch (code ?? '') {
    case COMPENSATION_LEDGER_CODES.draft_overlap:
      return 'draft_overlap';
    case COMPENSATION_LEDGER_CODES.same_date:
      return 'same_date';
    case COMPENSATION_LEDGER_CODES.currency_mid_period:
      return 'currency_mid_period';
    default:
      return 'other';
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : '';
}

/** Classifies a caught schedule/cancel error -- see module docblock. */
export function classifyCompensationMutationFailure(
  err: unknown,
): CompensationMutationFailure {
  const code = extractLedgerCode(err);
  if (code === COMPENSATION_LEDGER_CODES.consumed) {
    return { kind: 'consumed', message: errorMessage(err) };
  }
  if (isConflictTrpcError(err)) {
    return {
      kind: 'conflict',
      reason: conflictReason(code),
      message: errorMessage(err),
    };
  }
  if (isPayrollAddonRequiredError(err)) {
    return { kind: 'entitlement_required' };
  }
  if (isBadRequestTrpcError(err)) {
    return { kind: 'validation', message: errorMessage(err) };
  }
  return { kind: 'error' };
}

export interface EmployeeCompensationMutationDeps {
  scheduleCompensationChange: (
    input: {
      firm_id: string;
      entity_id: string;
      employee_id: string;
    } & ScheduleCompensationInput,
  ) => Promise<ScheduleCompensationResponse>;
  cancelCompensationChange: (input: {
    firm_id: string;
    entity_id: string;
    compensation_id: string;
    note?: string;
  }) => Promise<{ ok: boolean }>;
}

function defaultMutationDeps(): EmployeeCompensationMutationDeps {
  const client = getTrpcClient();
  return {
    scheduleCompensationChange: (input) =>
      client.payroll.scheduleCompensationChange.mutate(input),
    cancelCompensationChange: (input) =>
      client.payroll.cancelCompensationChange.mutate(input),
  };
}

/** Whether `effective_from` is today or earlier in UTC (`today_iso` injected for tests). */
export function isEffectiveNow(
  effective_from: string,
  today_iso: string,
): boolean {
  return effective_from <= today_iso;
}

/** Never throws. `today_iso` is injectable so the applied-now classification is deterministic in tests. */
export async function scheduleEmployeeCompensationChange(
  entityInput: { firm_id: string; entity_id: string; employee_id: string },
  change: ScheduleCompensationInput,
  deps?: EmployeeCompensationMutationDeps,
  today_iso: string = new Date().toISOString().slice(0, 10),
): Promise<ScheduleCompensationResult> {
  try {
    const resolvedDeps = deps ?? defaultMutationDeps();
    const response = await resolvedDeps.scheduleCompensationChange({
      ...entityInput,
      ...change,
    });
    return {
      outcome: 'success',
      applied_now: isEffectiveNow(change.effective_from, today_iso),
      back_pay_pay_runs: response.back_pay_preview?.finalised_pay_runs ?? [],
    };
  } catch (err) {
    console.error('[wagepro-web] Failed to schedule compensation change:', err);
    return {
      outcome: 'failure',
      failure: classifyCompensationMutationFailure(err),
    };
  }
}

/** Never throws. */
export async function cancelEmployeeCompensationChange(
  entityInput: { firm_id: string; entity_id: string },
  compensationId: string,
  deps?: EmployeeCompensationMutationDeps,
): Promise<CancelCompensationResult> {
  try {
    const resolvedDeps = deps ?? defaultMutationDeps();
    await resolvedDeps.cancelCompensationChange({
      firm_id: entityInput.firm_id,
      entity_id: entityInput.entity_id,
      compensation_id: compensationId,
    });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] Failed to cancel compensation change:', err);
    return {
      outcome: 'failure',
      failure: classifyCompensationMutationFailure(err),
    };
  }
}

export interface UseEmployeeCompensationResult {
  state: CompensationState;
  rows: EmployeeCompensationRecord[];
  has_history: boolean;
  effective_today: EffectiveCompensation | null;
  is_loading: boolean;
  retry: () => void;
  /** `undefined` when the write path is unreachable (read-only grant, no entity). */
  schedule_change:
    | ((
        input: ScheduleCompensationInput,
      ) => Promise<ScheduleCompensationResult>)
    | undefined;
  /** `undefined` for the same reasons as {@link schedule_change}. */
  cancel_change:
    | ((compensationId: string) => Promise<CancelCompensationResult>)
    | undefined;
  /** `!isWriteCapableGrant(grant)` -- hide the change/cancel affordances entirely when `true`. */
  is_read_only_grant: boolean;
}

/** Fetches and manages the effective-dated compensation history of one employee. */
export function useEmployeeCompensation(
  employeeId: string | null,
): UseEmployeeCompensationResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  // Keyed by employee id so navigating between profiles never renders the
  // previous employee's history while the next fetch is in flight; a refetch
  // for the SAME employee (after a mutation) keeps showing the old rows until
  // the new ones arrive, so the card does not flash to a skeleton.
  const [loaded, setLoaded] = React.useState<
    { employee_id: string; data: EmployeeCompensationListResult } | undefined
  >(undefined);
  const data = loaded?.employee_id === employeeId ? loaded.data : undefined;
  const [failure, setFailure] = React.useState<
    CompensationLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready' || employeeId === null) return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const empId: string = employeeId;
    let cancelled = false;
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchEmployeeCompensation(
        {
          firm_id: firmId,
          entity_id: entityId,
          employee_id: empId,
          include_inactive: true,
        },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setLoaded({ employee_id: empId, data: result.data });
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, employeeId, reloadToken]);

  const state = React.useMemo(
    () => resolveCompensationState({ entityContext, data, failure }),
    [entityContext, data, failure],
  );

  const readyEntity = entityContext.status === 'ready' ? entityContext : null;
  const canWrite =
    readyEntity !== null && employeeId !== null && isWriteCapableGrant(grant);

  const schedule_change = canWrite
    ? async (
        input: ScheduleCompensationInput,
      ): Promise<ScheduleCompensationResult> => {
        const result = await scheduleEmployeeCompensationChange(
          {
            firm_id: readyEntity.firm_id,
            entity_id: readyEntity.entity_id,
            employee_id: employeeId,
          },
          input,
        );
        if (result.outcome === 'success') {
          setReloadToken((n) => n + 1);
        }
        return result;
      }
    : undefined;

  const cancel_change = canWrite
    ? async (compensationId: string): Promise<CancelCompensationResult> => {
        const result = await cancelEmployeeCompensationChange(
          {
            firm_id: readyEntity.firm_id,
            entity_id: readyEntity.entity_id,
          },
          compensationId,
        );
        if (result.outcome === 'success') {
          setReloadToken((n) => n + 1);
        }
        return result;
      }
    : undefined;

  return {
    state,
    rows: state.status === 'ready' ? state.rows : [],
    has_history: state.status === 'ready' ? state.has_history : false,
    effective_today: state.status === 'ready' ? state.effective_today : null,
    is_loading: state.status === 'loading',
    retry: () => {
      setReloadToken((n) => n + 1);
    },
    schedule_change,
    cancel_change,
    is_read_only_grant: !isWriteCapableGrant(grant),
  };
}
